// File: frontend/src/components/registration/RegisterModal.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import "./Modal.css";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { FaCheck } from "react-icons/fa";
import {
  REDIRECT_DELAY_MS,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
} from "../../constants";
import { getLegalVersions, LEGAL_ROUTES } from "../../services/legalService";
import { InputClearButton } from "../common/InputClearButton";

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin?: () => void;
}

// Password strength type
type PasswordStrength = "weak" | "medium" | "strong";

/**
 * Calculate password strength based on length and character variety
 * Returns: weak | medium | strong
 */
const calculatePasswordStrength = (password: string): PasswordStrength => {
  if (!password || password.length < PASSWORD_MIN_LENGTH) return "weak";

  let score = 0;

  // Length points
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;

  // Character variety points
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  // eslint-disable-next-line no-useless-escape
  if (/[!@#$%^&*()_+\-=\[\]{};':\"\\|,.<>\/?]/.test(password)) score += 1;

  if (score <= 2) return "weak";
  if (score <= 4) return "medium";
  return "strong";
};

// Username validation regex
// Lowercase Turkish letters (a-z + çğıöşü), digits, underscores, and single spaces
// between words — no leading/trailing/consecutive spaces.
// Mirrors backend SettingConstants.USERNAME_PATTERN — keep in sync.
const USERNAME_REGEX = /^[a-zçğıöşü0-9_]+( [a-zçğıöşü0-9_]+)*$/;
const USERNAME_MIN_LENGTH = 3;
// Fallback max length — the effective value comes from the admin-configurable
// "user.username.max_length" system setting via useSiteSettings (hard ceiling 50)
const USERNAME_MAX_LENGTH_FALLBACK = 50;

// Email regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Password constants now live in constants/settings.constants.ts and mirror
// SettingConstants.java on the backend — see PASSWORD_MIN_LENGTH / PASSWORD_MAX_LENGTH

export const RegisterModal: React.FC<RegisterModalProps> = ({
  isOpen,
  onClose,
  onSwitchToLogin,
}) => {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  // Admin-configurable username max length from public site settings
  // legalMinAge feeds the age declaration line so it always matches the
  // value the backend actually enforces
  const { usernameMaxLength: usernameMaxLengthSetting, legalMinAge } = useSiteSettings();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  // Legal consent — both are required, and both are recorded on the account
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [confirmedAge, setConfirmedAge] = useState(false);
  // Version of the Terms text currently displayed; sent back with the
  // registration so consent is tied to a concrete document
  const [termsVersion, setTermsVersion] = useState<string | undefined>(undefined);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  // When true, the backend sent a verification email — switch to the check-email screen
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  // Store email so we can show it on the verification screen
  const [registeredEmail, setRegisteredEmail] = useState("");

  // Calculate password strength (memoized for performance)
  const passwordStrength = useMemo(
    () => calculatePasswordStrength(formData.password),
    [formData.password]
  );

  // Close modal on Escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ username: "", email: "", password: "", confirmPassword: "" });
      setErrors({});
      setTouched({});
      setSuccessMessage("");
      setEmailVerificationSent(false);
      setRegisteredEmail("");
      // Consent must be given again on every fresh open — a leftover tick
      // from a previous session is not consent
      setAcceptedTerms(false);
      setConfirmedAge(false);
    }
  }, [isOpen]);

  // Read the current Terms version when the modal opens. A failure here is
  // not fatal: the backend stamps the published version when none is sent.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getLegalVersions(i18n.language)
      .then((versions) => {
        if (!cancelled) setTermsVersion(versions.TERMS);
      })
      .catch(() => {
        /* version unknown — backend falls back to the published one */
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, i18n.language]);

  /**
   * Validate a single field — returns error message or empty string
   */
  // Effective username max length — admin-configurable via system settings
  const usernameMaxLength = usernameMaxLengthSetting || USERNAME_MAX_LENGTH_FALLBACK;

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "username": {
        if (!value.trim()) return t("register_error_username_required");
        // Validate against the trimmed value so a trailing space while typing
        // a multi-word username doesn't flash an error mid-typing.
        // The trimmed value is what gets submitted (see handleSubmit).
        const trimmed = value.trim();
        if (trimmed.length < USERNAME_MIN_LENGTH) return t("register_error_username_min_length");
        if (trimmed.length > usernameMaxLength) return t("register_error_username_max_length", { max: usernameMaxLength });
        if (!USERNAME_REGEX.test(trimmed)) return t("register_error_username_invalid_chars");
        return "";
      }
      case "email":
        if (!value.trim()) return t("register_error_email_required");
        if (!EMAIL_REGEX.test(value)) return t("register_error_email_invalid");
        return "";
      case "password":
        if (!value) return t("register_error_password_required");
        if (value.length < PASSWORD_MIN_LENGTH)
          return t("register_error_password_min_length", { min: PASSWORD_MIN_LENGTH });
        // Upper bound mirrors the backend — without it the server rejects the
        // form after submit instead of the user being told while typing
        if (value.length > PASSWORD_MAX_LENGTH)
          return t("register_error_password_max_length", { max: PASSWORD_MAX_LENGTH });
        return "";
      case "confirmPassword":
        if (!value) return t("register_error_confirm_password_required");
        if (value !== formData.password) return t("register_error_passwords_not_match");
        return "";
      default:
        return "";
    }
  };

  /**
   * Validate all form fields — returns true if valid
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Empty a field in one click, then hand focus back to it.
   * Runs the same validation path as handleChange so an emptied required field
   * reports itself immediately instead of silently looking fine.
   */
  const clearField = (name: keyof typeof formData) => {
    setFormData((prev) => ({ ...prev, [name]: "" }));
    setErrors((prev) => ({
      ...prev,
      general: "",
      [name]: touched[name] ? validateField(name, "") : "",
    }));
    document.getElementById(name)?.focus();
  };

  /**
   * Handle input change with real-time validation for touched fields
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    // Force lowercase for username — Turkish locale so "İ" → "i" and "I" → "ı"
    const value = name === "username" ? e.target.value.toLocaleLowerCase("tr") : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors.general) setErrors((prev) => ({ ...prev, general: "" }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }

    // Re-validate confirmPassword when password changes
    if (name === "password" && touched.confirmPassword && formData.confirmPassword) {
      const confirmError = value !== formData.confirmPassword
        ? t("register_error_passwords_not_match")
        : "";
      setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  /**
   * Mark field as touched and validate on blur
   */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  /**
   * Map backend error codes to field-level errors
   */
  const parseBackendError = (errorMessage: string): Record<string, string> => {
    const lower = errorMessage.toLowerCase();
    if (lower.includes("email") && (lower.includes("exist") || lower.includes("taken") || lower.includes("already"))) {
      return { email: t("register_error_email_exists") };
    }
    if (lower.includes("username") && (lower.includes("exist") || lower.includes("taken") || lower.includes("already"))) {
      return { username: t("register_error_username_exists") };
    }
    // Password rule violations are mapped onto the password field so the error
    // appears next to the input that has to change, not as a general banner
    if (errorMessage === "PASSWORD_REQUIRED") {
      return { password: t("register_error_password_required") };
    }
    if (errorMessage.startsWith("PASSWORD_TOO_SHORT:")) {
      return { password: t("PASSWORD_TOO_SHORT", { min: errorMessage.split(":")[1] }) };
    }
    if (errorMessage.startsWith("PASSWORD_TOO_LONG:")) {
      return { password: t("PASSWORD_TOO_LONG", { max: errorMessage.split(":")[1] }) };
    }
    if (errorMessage === "REGISTRATION_DISABLED") {
      return { general: t("REGISTRATION_DISABLED") };
    }
    // Consent rejections are mapped onto the checkbox that was missed, so the
    // user sees the error next to the control they need to fix
    if (errorMessage === "TERMS_NOT_ACCEPTED") {
      return { acceptedTerms: t("register_error_terms_required", "Devam etmek için kullanım koşullarını kabul etmelisiniz.") };
    }
    if (errorMessage === "AGE_NOT_CONFIRMED") {
      return { confirmedAge: t("register_error_age_required", "Yaş beyanında bulunmadan kayıt olamazsınız.") };
    }
    return { general: errorMessage };
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage("");
    setEmailVerificationSent(false);

    setTouched({ username: true, email: true, password: true, confirmPassword: true });
    if (!validateForm()) return;

    // Client-side consent gate. The backend enforces this too — this check
    // exists to point at the right checkbox, not to be the guarantee.
    if (!confirmedAge || !acceptedTerms) {
      setErrors({
        confirmedAge: !confirmedAge
          ? t("register_error_age_required", "Yaş beyanında bulunmadan kayıt olamazsınız.")
          : "",
        acceptedTerms: !acceptedTerms
          ? t("register_error_terms_required", "Devam etmek için kullanım koşullarını kabul etmelisiniz.")
          : "",
      });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const { requiresEmailVerification } = await register(
        formData.username.trim(),
        formData.email,
        formData.password,
        { acceptedTerms, confirmedAge, termsVersion }
      );

      if (requiresEmailVerification) {
        // Admin has email verification enabled — switch to the check-email screen.
        // Do NOT redirect to login; user must verify their email first.
        setRegisteredEmail(formData.email);
        setEmailVerificationSent(true);
      } else {
        // Instant registration — show success and redirect to login modal
        setSuccessMessage(t("register_success_message"));
        setTimeout(() => {
          onClose();
          if (onSwitchToLogin) onSwitchToLogin();
        }, REDIRECT_DELAY_MS);
      }
    } catch (error) {
      console.error("Registration failed:", error);
      const errorMessage = error instanceof Error ? error.message : t("register_error_general");
      const parsedErrors = parseBackendError(errorMessage);
      setErrors(parsedErrors);
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthColor = (): string => {
    switch (passwordStrength) {
      case "weak":   return "var(--color-error)";
      case "medium": return "var(--color-warning)";
      case "strong": return "var(--color-success)";
      default:       return "var(--color-gray-150)";
    }
  };

  const getStrengthWidth = (): string => {
    switch (passwordStrength) {
      case "weak":   return "33%";
      case "medium": return "66%";
      case "strong": return "100%";
      default:       return "0%";
    }
  };

  const getStrengthText = (): string => {
    if (!formData.password) return "";
    switch (passwordStrength) {
      case "weak":   return t("password_strength_weak");
      case "medium": return t("password_strength_medium");
      case "strong": return t("password_strength_strong");
      default:       return "";
    }
  };

  if (!isOpen) return null;

  // ── Email verification sent — same modal shell, different content ──
  if (emailVerificationSent) {
    return (
      <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="modal-container">
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>

          <div className="modal-header">
            <h2>{t("register_verify_email_title")}</h2>
            <p>{t("register_verify_email_subtitle", { email: registeredEmail })}</p>
          </div>

          <form className="modal-form" onSubmit={(e) => e.preventDefault()}>
            <p style={{ color: "var(--text-muted)", fontSize: "var(--font-size-md)", lineHeight: 1.6, marginTop: 0, marginBottom: "var(--spacing-xl)" }}>
              {t("register_verify_email_hint")}
            </p>
            <button
              type="button"
              className="submit-btn"
              onClick={() => { onClose(); if (onSwitchToLogin) onSwitchToLogin(); }}
            >
              {t("register_verify_email_go_login")}
            </button>
          </form>

          <div className="modal-footer">
            <p>
              {t("register_have_account")}{" "}
              <button type="button" className="link-btn" onClick={() => { onClose(); if (onSwitchToLogin) onSwitchToLogin(); }}>
                {t("register_login_link")}
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal registration form ──
  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-container">
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>

        <div className="modal-header">
          <h2>{t("register_title")}</h2>
        </div>

        <form onSubmit={handleSubmit} className="modal-form" noValidate>
          {successMessage && <div className="success-message">{successMessage}</div>}
          {errors.general && <div className="error-message general-error">{errors.general}</div>}

          {/* Username */}
          <div className="form-group">
            <label htmlFor="username">{t("register_username_label")}</label>
            <div className="input-with-counter input-with-clear">
              <input
                type="text" id="username" name="username"
                value={formData.username}
                onChange={handleChange} onBlur={handleBlur}
                className={errors.username && touched.username ? "input-error" : ""}
                placeholder={t("register_username_placeholder")}
                maxLength={usernameMaxLength} disabled={isLoading}
              />
              <span className="char-count">{formData.username.length}/{usernameMaxLength}</span>
              {formData.username && (
                <InputClearButton onClear={() => clearField("username")} />
              )}
            </div>
            {errors.username && touched.username && <span className="field-error">{errors.username}</span>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">{t("register_email_label")}</label>
            <div className="input-with-clear">
              <input
              type="email" id="email" name="email"
              value={formData.email}
              onChange={handleChange} onBlur={handleBlur}
              className={errors.email && touched.email ? "input-error" : ""}
              placeholder={t("register_email_placeholder")} disabled={isLoading}
            />
              {formData.email && (
                <InputClearButton onClear={() => clearField("email")} />
              )}
            </div>
            {errors.email && touched.email && <span className="field-error">{errors.email}</span>}
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password">{t("register_password_label")}</label>
            <div className="input-with-clear">
              <input
              type="password" id="password" name="password"
              value={formData.password}
              onChange={handleChange} onBlur={handleBlur}
              className={errors.password && touched.password ? "input-error" : ""}
              placeholder={t("register_password_placeholder")}
              disabled={isLoading}
            />
              {formData.password && (
                <InputClearButton onClear={() => clearField("password")} />
              )}
            </div>
            {/* Rules are stated up-front, not only after a failed submit.
                NOTE: deliberately no maxLength attribute on the input — the browser
                would silently truncate the value to the limit, the request would
                then be valid, and the user would never see why their password was
                shortened. The rule has to fail visibly instead. */}
            <span className={`field-hint${formData.password.length > PASSWORD_MAX_LENGTH ? " field-hint--over" : ""}`}>
              {t("password_rules_hint", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}
              {formData.password.length > 0 && ` (${formData.password.length}/${PASSWORD_MAX_LENGTH})`}
            </span>
            {formData.password && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div className="strength-fill" style={{ width: getStrengthWidth(), backgroundColor: getStrengthColor() }} />
                </div>
                <span className="strength-text" style={{ color: getStrengthColor() }}>{getStrengthText()}</span>
              </div>
            )}
            {errors.password && touched.password && <span className="field-error">{errors.password}</span>}
          </div>

          {/* Confirm password */}
          <div className="form-group">
            <label htmlFor="confirmPassword">{t("register_confirm_password_label")}</label>
            <div className="input-with-clear">
              <input
              type="password" id="confirmPassword" name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange} onBlur={handleBlur}
              className={errors.confirmPassword && touched.confirmPassword ? "input-error" : ""}
              placeholder={t("register_confirm_password_placeholder")}
              disabled={isLoading}
            />
              {formData.confirmPassword && (
                <InputClearButton onClear={() => clearField("confirmPassword")} />
              )}
            </div>
            {formData.confirmPassword && formData.password && touched.confirmPassword && !errors.confirmPassword && (
              <span className="field-success"><FaCheck size={12} /> {t("passwords_match")}</span>
            )}
            {errors.confirmPassword && touched.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
          </div>

          {/* Legal consent — two separate ticks because the platform makes two
              separate claims about the user: their age, and their agreement.
              The second tick covers the Terms, the community rules and the
              Privacy Policy together; only the Terms version is recorded on the
              account, because that is the document consent is measured against.
              Links open in a new tab so a half-filled form is never lost. */}
          <div className="consent-group">
            <label className="consent-row">
              <input
                type="checkbox"
                checked={confirmedAge}
                onChange={(e) => {
                  setConfirmedAge(e.target.checked);
                  if (e.target.checked) setErrors((prev) => ({ ...prev, confirmedAge: "" }));
                }}
                disabled={isLoading}
              />
              <span className="consent-text">
                {t("register_consent_age", { age: legalMinAge, defaultValue: "{{age}} yaşından büyük olduğumu beyan ederim." })}
              </span>
            </label>
            {errors.confirmedAge && <span className="field-error">{errors.confirmedAge}</span>}

            <label className="consent-row">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked);
                  if (e.target.checked) setErrors((prev) => ({ ...prev, acceptedTerms: "" }));
                }}
                disabled={isLoading}
              />
              <span className="consent-text">
                {/* Prefix + suffix are separate keys because Turkish puts the verb
                    after the object and English puts it before — a single
                    interpolated sentence cannot satisfy both word orders. */}
                {t("register_consent_prefix", "")}
                <Link to={LEGAL_ROUTES.TERMS} target="_blank" rel="noopener noreferrer" className="consent-link">
                  {t("legal_link_terms", "Kullanım Koşulları")}
                </Link>
                {t("register_consent_sep", ", ")}
                <Link to={LEGAL_ROUTES.RULES} target="_blank" rel="noopener noreferrer" className="consent-link">
                  {t("legal_link_rules", "Hamur Abi Kuralları")}
                </Link>
                {t("register_consent_and", " ve ")}
                <Link to={LEGAL_ROUTES.PRIVACY} target="_blank" rel="noopener noreferrer" className="consent-link">
                  {t("legal_link_privacy", "Gizlilik Politikası")}
                </Link>
                {t("register_consent_terms", "\u0027nı okudum ve kabul ediyorum.")}
              </span>
            </label>
            {errors.acceptedTerms && <span className="field-error">{errors.acceptedTerms}</span>}
          </div>

          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? t("register_button_loading") : t("register_button")}
          </button>
        </form>

        <div className="modal-footer">
          <p>
            {t("register_have_account")}{" "}
            <button type="button" className="link-btn" onClick={onSwitchToLogin} disabled={isLoading}>
              {t("register_login_link")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};